/**
 * The part of an action that crosses the ground.
 *
 * Between pressing a skill and the ground changing there is a journey, and leaving
 * it out is what makes a turn-based board feel like a spreadsheet: the numbers move
 * but nothing ever travels. A shot is a line that is already there; a bolt is an
 * object that flies and can be watched; a blessing falls from above. Each is a
 * different sentence about what just happened.
 *
 * The trace is the hero of the whole effect. The ground decal says where something
 * landed, which is bookkeeping; the trace is the event itself, and it is the only
 * part the eye is actually tracking. So everything here is built to be looked at
 * directly: a muzzle that fires, a body that flies and leaves a wake, a blade that
 * passes through, a shaft that comes down and lands.
 *
 * Everything is authored in grid space and projected by the board's own map,
 * exactly like the merged effects, so a projectile crosses the cells it appears to
 * cross at any zoom and under any rake.
 *
 * One thing here is not a style choice but a fix: trace materials own a full set of
 * lifecycle uniforms. They used to be handed only the projection block, which meant
 * `uActivation` defaulted to zero and every trace fragment discarded itself — the
 * traces were being built, animated and disposed of without ever putting a pixel on
 * the screen.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Mesh,
  PlaneGeometry,
  Points,
  ShaderMaterial,
  TextureLoader,
} from 'three';

import {
  EMISSION_CHUNK,
  INTENT_CHUNK,
  LIFECYCLE_CHUNK,
  NOISE_CHUNK,
  PARTICLE_PATH_CHUNK,
  PROJECTION_CHUNK,
  projectionOptions,
} from './shaders';
import { INTENTS } from './presentation';
import { normalizeViolationType, violationProfile } from './violation';

const textures = new Map();
const loader = new TextureLoader();

/**
 * The skill's own icon, loaded once per url.
 *
 * A missing icon is not an error worth stopping for — the trace still has its glow
 * and its colour, and a skill nobody has drawn yet should still be castable.
 */
export function spriteTexture(url) {
  if (!url) return null;
  if (textures.has(url)) return textures.get(url);
  const texture = loader.load(url, undefined, undefined, () => {
    textures.set(url, null);
  });
  texture.anisotropy = 4;
  textures.set(url, texture);
  return texture;
}

/**
 * The clock and the colours every part of one trace shares.
 *
 * One object, handed to every material in the trace, so the muzzle, the beam, the
 * wake and the sparks cannot drift out of step with each other by a frame.
 */
function traceUniforms(shared, cast) {
  const profile = violationProfile(cast.violationType, cast.path);
  /*
   * Traces carry intent too. Everything crossing the ground used to be coloured by
   * the violation alone, which is why a heal made of `Energy` arrived as the same
   * electric blue as an energy bolt — the material was right and the sentence was
   * wrong. The uniforms are declared for every trace; only the ones whose shader
   * asks for them read them.
   */
  const intent = INTENTS[cast.intent] || INTENTS.harm;
  return {
    ...shared,
    uTime: { value: 0 },
    uActivation: { value: 1 },
    uBurst: { value: 0 },
    uDissolve: { value: 0 },
    uGrowth: { value: 1 },
    uChar: { value: 0 },
    uIntensity: { value: cast.intensity ?? 0.8 },
    uAreaNorm: { value: 1 },
    uSeed: { value: ((cast.seed || 0) % 1000) * 0.137 },
    uSince: { value: 0 },
    uOctaves: { value: 4 },
    uProgress: { value: 0 },
    uFade: { value: 1 },
    uColorA: { value: new Color(profile.color) },
    uColorB: { value: new Color(profile.deep) },
    uColorCore: { value: new Color(profile.core || '#ffffff') },
    uIntent: { value: intent.index },
    uIntentBias: { value: new Color(intent.bias || profile.color) },
    uIntentAmount: { value: intent.biasAmount },
    uLift: { value: intent.lift },
    uConverge: { value: intent.converge },
  };
}

/**
 * How high off the floor a shot travels, in cells.
 *
 * Held at the figures' own mass rather than at the boards' surface: a beam grazing
 * the floor reads as a line painted on the ground, and the thing it is supposed to
 * be crossing is a person standing on a plinth.
 */
const BEAM_HEIGHT = 0.62;

const TRACE_HEADER = /* glsl */`
  uniform float uProgress;
  uniform float uFade;
`;

const GRID_VERTEX = /* glsl */`
  ${PROJECTION_CHUNK}
  varying vec2 vUvv;
  void main(){
    vUvv = uv;
    gl_Position = gridToClip(gridPosition(position));
  }
`;

/**
 * The shot.
 *
 * A bullet or a bolt of force is not an object crossing the board — by the time the
 * eye finds it, it is already a line from the muzzle to the wound. So it arrives
 * whole in two frames, tapers with distance, and is gone inside a seventh of a
 * second, leaving the air along its path bent for a moment afterwards.
 *
 * The core is drawn nearly white and much narrower than the glow around it. That
 * ratio is the entire difference between a beam and a coloured stripe: a real
 * emitter is small and violently bright, and what you actually see is the halo it
 * throws into the air around itself.
 *
 * The width is measured on the SCREEN, not on the floor. A ribbon authored flat in
 * grid space is foreshortened by the board's rake — on these maps a cell is twice
 * as wide as it is deep — so a beam a third of a cell across arrived as a four-pixel
 * hairline with its halo squeezed to nothing, which is what made a shot read as a
 * drawn line rather than as light in the air. Here the spine is projected first and
 * the band is built perpendicular to it in pixels, so the beam has the same body at
 * any rake, any zoom and in any direction.
 */
const BEAM_VERTEX = /* glsl */`
  ${PROJECTION_CHUNK}
  uniform vec3 uFrom;
  uniform vec3 uTo;
  uniform float uStartHalf;
  uniform float uEndHalf;
  varying vec2 vUvv;

#ifdef BM3D
  void main(){
    vUvv = uv;
    vec4 ca = gridToClip(uFrom);
    vec4 cb = gridToClip(uTo);
    vec2 half = uViewport * 0.5;
    vec2 a = ca.xy / ca.w * half;
    vec2 b = cb.xy / cb.w * half;
    vec2 run = b - a;
    vec2 dir = run / max(length(run), 0.0001);
    vec2 side = vec2(-dir.y, dir.x);
    vec4 clip = mix(ca, cb, uv.y);
    float band = mix(uStartHalf, uEndHalf, uv.y) * uCellPx;
    vec2 offset = side * (uv.x * 2.0 - 1.0) * band / half;
    gl_Position = vec4(clip.xy + offset * clip.w, clip.z, clip.w);
  }
#else
  vec2 toScreen(vec3 p){
    vec2 s = gridToScreen(p.xz);
    s.y -= p.y * uCellPx;
    return s;
  }

  void main(){
    vUvv = uv;
    vec2 a = toScreen(uFrom);
    vec2 b = toScreen(uTo);
    vec2 run = b - a;
    vec2 dir = run / max(length(run), 0.0001);
    vec2 side = vec2(-dir.y, dir.x);
    vec2 centre = mix(a, b, uv.y);
    float band = mix(uStartHalf, uEndHalf, uv.y) * uCellPx;
    vec2 p = centre + side * (uv.x * 2.0 - 1.0) * band;
    gl_Position = vec4(p.x / uViewport.x * 2.0 - 1.0, 1.0 - p.y / uViewport.y * 2.0, 0.0, 1.0);
  }
#endif
`;

/**
 * `uTracer` is the one thing separating a slug from a beam.
 *
 * Physical damage at range is not a line of light — it is a piece of metal, and
 * drawing it as a continuous shaft makes a rifle shot read as the same spell as a
 * bolt of holy light in a different colour. At 1 the shaft collapses to a short
 * bright body running the line with a smear behind it; at 0 the whole length is
 * written out from the muzzle at once, as light is.
 */
const BEAM_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  uniform float uTracer;
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}
  ${NOISE_CHUNK}

  void main(){
    float across = 1.0 - abs(vUvv.x * 2.0 - 1.0);
    float along = vUvv.y;

    /* Light: the whole shaft writes itself out from the muzzle, fast but catchable. */
    float head = uProgress * 1.35;
    float written = smoothstep(head, head - 0.32, along);

    /* Metal: a body with a smear behind it, and nothing at all in front. */
    float body = exp(-pow((along - uProgress) * 6.0, 2.0));
    float smear = smoothstep(uProgress - 0.34, uProgress, along) * (1.0 - smoothstep(uProgress, uProgress + 0.035, along));
    float slug = clamp(body * 1.5 + smear * 0.55, 0.0, 1.6);

    float drawn = mix(written, slug, uTracer);

    /* Air still moving where it passed: only visible once the beam itself is going. */
    float shimmer = fbm(vec2(along * 7.0, uTime * 3.1 + uSeed)) - 0.5;
    float bend = clamp(across + shimmer * 0.22 * (1.0 - uFade), 0.0, 1.0);

    float core = pow(bend, 6.0);
    float halo = pow(bend, 1.7);
    /*
     * The outermost term exists to be nearly invisible. Without it the band has an
     * edge where the halo runs out, and an edge is the difference between light and
     * a painted stripe; the floor added to the heat term is what keeps that outer air the
     * violation's own colour instead of the cooled-out deep it ramps from.
     */
    float envelope = pow(bend, 0.6);

    float heat = 0.16 + core * 1.15 + halo * 0.4;
    vec3 color = emberRamp(heat);

    float alpha = (core * 1.15 + halo * 0.42 + envelope * 0.14) * drawn * uFade;
    /* Brightest at the muzzle: the far end of a shot has already spent itself. */
    alpha *= 1.2 - along * 0.35;
    alpha *= uActivation * (0.5 + 0.6 * uIntensity);
    if (alpha < 0.004) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/** A bolt's wake: hottest at the head, cooling and thinning back along the path. */
const WAKE_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}
  ${NOISE_CHUNK}

  void main(){
    float across = 1.0 - abs(vUvv.x * 2.0 - 1.0);
    float age = vUvv.y;

    float taper = pow(1.0 - age, 1.6);
    float body = pow(across, 2.0) * taper;
    float core = pow(across, 9.0) * pow(1.0 - age, 3.2);
    float boil = 0.75 + 0.5 * fbm(vec2(age * 6.0 - uTime * 2.4, uSeed));

    vec3 color = emberRamp(core * 1.4 + body * 0.3);
    float alpha = (core * 1.1 + body * 0.5) * boil * uFade;
    alpha *= uActivation * (0.5 + 0.6 * uIntensity);
    if (alpha < 0.004) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * Something coming down onto a cell, or lifting off it.
 *
 * Two crossed curtains rather than one: a single quad is a flat card the moment the
 * board is rotated off its axis, and the cross reads as a body of lit air from
 * anywhere. The shaft holds, and a bright wavefront runs down it and lands.
 */
const SHAFT_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}
  ${NOISE_CHUNK}

  void main(){
    float across = 1.0 - abs(vUvv.x * 2.0 - 1.0);
    float up = vUvv.y;

    float column = pow(across, 2.6) * (1.0 - pow(up, 2.2) * 0.55);
    float inner = pow(across, 9.0) * (1.0 - up * 0.6);

    /* The front falls the length of the shaft and blooms where it meets the floor. */
    float front = exp(-pow((up - (1.0 - uProgress)) * 4.2, 2.0));
    float land = smoothstep(0.8, 1.0, uProgress) * exp(-up * 5.0);

    float drift = 0.7 + 0.5 * fbm(vec2(up * 3.2 - uTime * 0.9, uSeed + across * 2.0));
    float heat = inner + front * 0.9 + land;
    vec3 color = emberRamp(heat);

    /*
     * Every term is shaped by the SAME falloff across the curtain. A term that
     * fades linearly to its edge still has an edge — the descending wavefront was
     * rendering as a grey rectangle with two visible sides, which is the quad it is
     * drawn on showing through.
     */
    float soft = pow(across, 2.2);
    float alpha = (column * 0.34 + inner * 0.7 + front * soft * 1.1 + land * soft * 1.4) * drift;
    /* And nothing reaches the top of the curtain, so the geometry never ends. */
    alpha *= smoothstep(1.0, 0.72, up);
    alpha *= uFade * uActivation * (0.45 + 0.7 * uIntensity);
    if (alpha < 0.004) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * The swing.
 *
 * A slash is read almost entirely off its leading edge — the bright line that is
 * where the blade IS — with everything behind it a smear that has already happened.
 * So the sweep runs a hard front around the arc and trails a wedge behind it, and
 * the arc is centred on the target rather than in front of it: the blade passes
 * THROUGH the thing it hits.
 */
const SLASH_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}

  void main(){
    float sweep = vUvv.x;
    float across = 1.0 - abs(vUvv.y * 2.0 - 1.0);

    /*
     * A wedge, not a hairline.
     *
     * The bright band has to be wide enough to survive one frame at 60fps — a
     * five-percent sliver of the arc is fourteen pixels of a two-hundred-and-eighty
     * pixel sweep, which at speed is a flicker nobody registers. So the front is a
     * third of the arc long, hottest at its leading edge, dragging a cooling smear.
     */
    float lead = smoothstep(uProgress - 0.3, uProgress - 0.02, sweep);
    float ahead = 1.0 - smoothstep(uProgress, uProgress + 0.05, sweep);
    float front = lead * ahead;
    float trail = smoothstep(uProgress - 0.85, uProgress, sweep) * ahead;
    float edgeLine = exp(-pow((sweep - uProgress + 0.02) * 24.0, 2.0)) * ahead;

    /* The blade is thin where it started and thin where it ends. */
    float belly = pow(across, 3.0) * (0.35 + 0.65 * sin(clamp(sweep, 0.0, 1.0) * 3.14159));
    float edge = pow(across, 9.0);

    /*
     * Only the leading edge runs white. Driving the whole front past the top of the
     * ramp paints the swing in the core colour end to end, and a white smear carries no
     * material at all — a frost blade and a burning one come out identical.
     */
    float heat = front * 0.42 + edgeLine * 0.78;
    vec3 color = emberRamp(heat);
    float alpha = (front * belly * 0.9 + edgeLine * (edge * 2.4 + belly * 0.5) + trail * belly * 0.3) * uFade;
    alpha *= uActivation * (0.6 + 0.7 * uIntensity);
    if (alpha < 0.004) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * Anything billboarded: the muzzle, and the head of a bolt.
 *
 * Projected first and built around that point in pixels, so it faces the viewer
 * whatever the board is doing underneath it.
 */
const BILLBOARD_VERTEX = /* glsl */`
  ${PROJECTION_CHUNK}
  uniform vec3 uHead;
  uniform float uScale;
  varying vec2 vUvv;
  void main(){
    vUvv = uv;
#ifdef BM3D
    vec4 clip = gridToClip(uHead);
    vec2 offset = vec2(position.x, position.y) * uScale * uCellPx / (uViewport * 0.5);
    gl_Position = vec4(clip.xy + offset * clip.w, clip.z, clip.w);
#else
    vec2 centre = gridToScreen(uHead.xz);
    centre.y -= uHead.y * uCellPx;
    vec2 corner = centre + position.xy * uScale * uCellPx;
    gl_Position = vec4(corner.x / uViewport.x * 2.0 - 1.0, 1.0 - corner.y / uViewport.y * 2.0, 0.0, 1.0);
#endif
  }
`;

/**
 * The muzzle.
 *
 * A weapon discharging is the brightest single moment in the whole action and it is
 * over in four frames. It is a hot point, an anisotropic flare stretched along the
 * firing line, and a short cone of blown light in front of it — no ring, because a
 * ring is the one shape a discharge never makes.
 */
/**
 * The commit: a body closing on something.
 *
 * Not a beam, and deliberately not shaped like one. A beam is brightest where it
 * left and hangs in the air along its whole length; this is a low ground-hugging
 * wedge with nothing ahead of its front and a short smear behind it, so what the eye
 * follows is a leading edge crossing ground rather than a line being switched on.
 * It lives at ankle height because that is where a charge kicks dust, and because a
 * streak drawn at chest height is indistinguishable from a bolt.
 */
const CHARGE_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}

  void main(){
    float along = vUvv.y;
    float across = 1.0 - abs(vUvv.x * 2.0 - 1.0);

    float behind = smoothstep(uProgress - 0.62, uProgress - 0.04, along);
    float ahead = 1.0 - smoothstep(uProgress - 0.02, uProgress + 0.07, along);
    float body = behind * ahead;
    float edge = exp(-pow((along - uProgress + 0.04) * 15.0, 2.0)) * ahead;

    /* Thin and hot down the spine, hazy at the sides — a wedge, not a tube. */
    float spine = pow(across, 5.0);
    float haze = pow(across, 1.5);

    float heat = body * spine * 0.7 + edge * 0.85;
    vec3 color = emberRamp(heat);

    float alpha = (body * (haze * 0.24 + spine * 0.62) + edge * (haze * 0.5 + spine * 0.9)) * uFade;
    alpha *= uActivation * (0.5 + 0.65 * uIntensity);
    if (alpha < 0.005) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

const MUZZLE_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  uniform vec2 uAxis;
  uniform float uMuzzle;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}

  void main(){
    vec2 q = (vUvv - 0.5) * 2.0;
    float life = clamp(uMuzzle, 0.0, 1.0);
    float punch = pow(1.0 - life, 2.4);

    float along = dot(q, uAxis);
    float side = dot(q, vec2(-uAxis.y, uAxis.x));

    float point = exp(-dot(q, q) * (16.0 + life * 44.0));
    float flare = exp(-(along * along * 1.1 + side * side * 46.0)) * (0.5 + 0.5 * punch);
    float cone = exp(-(pow(max(along, 0.0) - 0.15, 2.0) * 7.0 + side * side * (9.0 + max(along, 0.0) * 26.0)));

    float heat = point * 1.6 + flare * 0.7 + cone * 0.5;
    vec3 color = emberRamp(heat);
    /*
     * Gains kept under one at the centre on purpose. A discharge whose terms sum
     * past the clamp paints a flat white plateau, and the shape you then see is the
     * iso-contour where the sum crosses one — a hard-edged polygon sitting on the
     * board. Staying inside the clamp lets the falloff itself be the shape.
     */
    float alpha = (point * 0.95 + flare * 0.5 + cone * 0.42) * punch;
    alpha *= uActivation * (0.6 + 0.5 * uIntensity);
    if (alpha < 0.006) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

const HEAD_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  uniform sampler2D uSprite;
  uniform int uHasSprite;
  uniform float uSpin;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}

  void main(){
    vec2 q = vUvv - 0.5;
    float d = length(q);
    float core = exp(-d * d * 90.0);
    float bloom = exp(-d * d * 11.0);
    vec3 color = emberRamp(core * 1.5 + bloom * 0.35);
    float alpha = core * 1.4 + bloom * 0.5;

    if (uHasSprite == 1){
      float c = cos(uSpin);
      float s = sin(uSpin);
      vec2 uv = vec2(q.x * c - q.y * s, q.x * s + q.y * c) * 1.7 + 0.5;
      if (uv.x > 0.0 && uv.x < 1.0 && uv.y > 0.0 && uv.y < 1.0){
        vec4 icon = texture2D(uSprite, uv);
        /* The icon rides the light rather than replacing it: it is tinted by the
           violation and never draws its own dark pixels over the glow. */
        color += icon.rgb * uColorCore * icon.a * 0.7;
        alpha = max(alpha, icon.a * 0.55 * bloom * 2.2);
      }
    }

    alpha *= uFade * uActivation * (0.5 + 0.6 * uIntensity);
    if (alpha < 0.006) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.2));
  }
`;

/**
 * Sparks shed along a flight path.
 *
 * The spawn point is not stored — it is recomputed from the same arc the head flies,
 * so a spark leaves the bolt exactly where the bolt was when it was born, at any
 * frame rate, with nothing uploaded per frame.
 */
const PATH_SPARK_VERTEX = /* glsl */`
  ${PROJECTION_CHUNK}
  ${LIFECYCLE_CHUNK}
  ${PARTICLE_PATH_CHUNK}
  attribute vec4 aSpark;
  attribute vec3 aVelocity;
  uniform vec3 uFrom;
  uniform vec3 uTo;
  uniform float uLift;
  varying float vLife;

  void main(){
    float born = aSpark.x;
    float age = uProgressSeconds - born * uFlightSeconds;
    if (age < 0.0 || age > aSpark.y){
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      vLife = 1.0;
      gl_PointSize = 0.0;
      return;
    }
    float eased = born * born * (3.0 - 2.0 * born);
    vec3 spawn = mix(uFrom, uTo, eased);
    spawn.y += sin(eased * 3.14159) * uLift;

    vec3 p = dragPath(spawn, aVelocity, 3.4, 2.6, age);
    vLife = clamp(age / aSpark.y, 0.0, 1.0);
    gl_Position = gridToClip(p);
    gl_PointSize = clamp(aSpark.z * (1.0 - vLife * 0.7) * uCellPx, 1.0, 22.0);
  }
`;

const SPARK_FRAGMENT = /* glsl */`
  precision highp float;
  varying float vLife;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}

  void main(){
    vec2 q = gl_PointCoord - 0.5;
    float d = dot(q, q);
    float core = exp(-d * 46.0);
    float glow = exp(-d * 9.0);
    float heat = (1.0 - vLife);
    vec3 color = emberRamp(core * heat * 1.6 + glow * 0.25);
    float alpha = (core * 1.3 + glow * 0.3) * pow(1.0 - vLife, 1.5) * uFade;
    alpha *= uActivation * (0.5 + 0.6 * uIntensity);
    if (alpha < 0.006) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * Motes on a radius: light going somewhere rather than light appearing.
 *
 * Three of the game's actions are not deliveries at all — nothing crosses the ground
 * between two people. A gathering DRAWS the air in to its subject, a displacement
 * pulls them out of the world and puts them back, and a summoning lets something
 * out. All three are radial: the direction of travel relative to a single point is
 * the entire statement, so one path integrator serves them and only the sign of the
 * velocity changes.
 *
 * The start point is the particle's own `position`, so an inward mote begins out on
 * its ring and an outward one begins at the centre; drag carries each exactly as far
 * as it was aimed and no further, which is what makes an arrival decelerate into
 * place instead of stopping dead.
 */
const RADIAL_VERTEX = /* glsl */`
  ${PROJECTION_CHUNK}
  ${LIFECYCLE_CHUNK}
  ${PARTICLE_PATH_CHUNK}
  uniform float uProgressSeconds;
  uniform float uDrag;
  uniform float uGravity;
  attribute vec3 aVelocity;
  attribute vec3 aMote;
  varying float vLife;

  void main(){
    float age = uProgressSeconds - aMote.x;
    if (age < 0.0 || age > aMote.y){
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vLife = 1.0;
      return;
    }
    vec3 p = dragPath(position, aVelocity, uDrag, uGravity, age);
    vLife = clamp(age / aMote.y, 0.0, 1.0);
    gl_Position = gridToClip(p);
    gl_PointSize = clamp(aMote.z * (0.55 + vLife * 0.7) * uCellPx, 1.0, 20.0);
  }
`;

/**
 * A mote is the opposite of a spark: it gets BRIGHTER as it goes.
 *
 * Debris is hottest when it is thrown and cools on the way out. Something being
 * drawn into a body is dim out on the edge and blinding by the time it arrives —
 * fade it like a spark and the gathering reads as a dispersal played backwards.
 */
const MOTE_FRAGMENT = /* glsl */`
  precision highp float;
  varying float vLife;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}
  ${INTENT_CHUNK}

  void main(){
    vec2 q = gl_PointCoord - 0.5;
    float d = dot(q, q);
    float core = exp(-d * 44.0);
    float glow = exp(-d * 8.0);
    float arriving = smoothstep(0.0, 0.85, vLife);
    /* Out only at the very end, so nothing pops off the screen mid-flight. */
    float tail = 1.0 - smoothstep(0.86, 1.0, vLife);
    /* Pushed further toward the intent than anything else in the trace: a mote has
       no shape to read, so its colour is the only thing that says what this is. */
    vec3 color = mix(emberRamp(core * (0.45 + arriving * 1.3) + glow * 0.24), uIntentBias, uIntentAmount * 0.72);
    /*
     * A mote's dimmest moment still has to be brighter than the map under it. At a
     * fifth of its arrival value it was losing to the floor texture and the cloud
     * read as noise in the picture rather than as light being drawn in.
     */
    float alpha = (core * 1.15 + glow * 0.34) * (0.42 + arriving * 1.1) * tail * uFade;
    alpha *= uActivation * (0.5 + 0.6 * uIntensity);
    if (alpha < 0.006) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * Leaving.
 *
 * A body that is about to not be there pinches: the column of light around it
 * narrows to a thread while what is left of it climbs and goes out at the top. The
 * width is driven by the exponent of the same falloff the shaft uses, so it thins
 * without ever acquiring a side — a collapse drawn as a shrinking shape would put
 * two hard edges on the board, which is the thing the whole renderer refuses.
 */
const COLLAPSE_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}
  ${NOISE_CHUNK}

  void main(){
    float across = 1.0 - abs(vUvv.x * 2.0 - 1.0);
    float up = vUvv.y;

    float pinch = pow(across, mix(3.0, 12.0, uProgress));
    float climb = exp(-pow((up - uProgress * 1.05) * 5.2, 2.0));
    float footing = exp(-up * 5.0) * (1.0 - uProgress);
    float drift = 0.78 + 0.36 * fbm(vec2(up * 2.6 - uTime * 0.7, uSeed));

    /*
     * Every gain stays well inside the clamp. Summed past one, these terms paint a
     * flat white plateau whose iso-contour is a hard triangle standing on the cell —
     * a silhouette, which is the one thing this renderer refuses. Under the clamp
     * the falloff itself is the shape.
     */
    vec3 color = emberRamp(pinch * (climb * 0.75 + 0.14) + footing * 0.3);
    float alpha = (pinch * (0.3 + climb * 0.9) + footing * pow(across, 2.5) * 0.45) * drift;
    alpha *= smoothstep(1.0, 0.55, up) * uFade * uActivation * (0.45 + 0.6 * uIntensity);
    if (alpha < 0.005) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * A way through.
 *
 * A summoning is not thrown and does not fall: something on the other side is let
 * in. So the seam opens over the first third, holds while whatever is owed comes
 * through, and closes behind it — hottest at its mouth and spilling light across the
 * floor at its sill, because a doorway with no light under it is a picture of a
 * doorway.
 */
const RIFT_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}
  ${NOISE_CHUNK}

  void main(){
    float across = 1.0 - abs(vUvv.x * 2.0 - 1.0);
    float up = vUvv.y;

    float open = smoothstep(0.0, 0.34, uProgress) * (1.0 - smoothstep(0.74, 1.0, uProgress));
    float seam = pow(across, mix(15.0, 4.5, open));
    float mouth = exp(-pow((up - 0.36) * 2.3, 2.0));
    float sill = exp(-up * 6.0);
    float grain = 0.7 + 0.5 * fbm(vec2(up * 3.4 - uTime * 0.8, uSeed + 3.0));

    vec3 color = emberRamp((seam * (mouth * 1.25 + 0.28) + sill * 0.55) * open);
    float alpha = (seam * (0.42 + mouth * 1.5) + sill * pow(across, 2.5) * 0.85) * open * grain;
    alpha *= smoothstep(1.0, 0.66, up) * uFade * uActivation * (0.5 + 0.7 * uIntensity);
    if (alpha < 0.005) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/*
 * Two-sided, always.
 *
 * The board's projection is a bilinear warp with a flipped vertical axis, so which
 * way a triangle winds on screen depends on where on the board it is and which way
 * it was turned — a swing aimed east and the same swing aimed west do not agree. A
 * single back-facing crescent is an effect that silently does not exist, which is
 * exactly what happened to the slash.
 */
function gridMaterial(uniforms, fragmentShader, { blending = AdditiveBlending } = {}) {
  return new ShaderMaterial({
    uniforms,
    vertexShader: GRID_VERTEX,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(uniforms),
    side: DoubleSide,
    blending,
  });
}

/**
 * A ribbon from one cell to another, tessellated so it follows the board's warp and
 * narrowing along its length: a shot is widest where it left the barrel.
 */
function taperedRibbon(from, to, startHalf, endHalf, segments = 28, low = 0.38, high = 0.45) {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const length = Math.hypot(dx, dz) || 1;
  const nx = -dz / length;
  const nz = dx / length;

  const positions = new Float32Array((segments + 1) * 2 * 3);
  const uvs = new Float32Array((segments + 1) * 2 * 2);
  const indices = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const halfWidth = startHalf + (endHalf - startHalf) * t;
    const cx = from.x + dx * t;
    const cz = from.z + dz * t;
    const base = i * 2;
    positions[base * 3] = cx - nx * halfWidth;
    positions[base * 3 + 1] = low;
    positions[base * 3 + 2] = cz - nz * halfWidth;
    positions[(base + 1) * 3] = cx + nx * halfWidth;
    positions[(base + 1) * 3 + 1] = high;
    positions[(base + 1) * 3 + 2] = cz + nz * halfWidth;
    uvs[base * 2] = 0;
    uvs[base * 2 + 1] = t;
    uvs[(base + 1) * 2] = 1;
    uvs[(base + 1) * 2 + 1] = t;
    if (i < segments) {
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

/**
 * The strip a screen-space beam is drawn on.
 *
 * Carries nothing but its parametrisation: `uv.y` runs from the muzzle to the
 * target and `uv.x` picks a side. Every actual coordinate is computed in the vertex
 * shader from the two endpoints, so the band cannot be foreshortened by the board.
 */
function beamStrip(segments = 24) {
  const positions = new Float32Array((segments + 1) * 2 * 3);
  const uvs = new Float32Array((segments + 1) * 2 * 2);
  const indices = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const base = i * 2;
    uvs[base * 2] = 0;
    uvs[base * 2 + 1] = t;
    uvs[(base + 1) * 2] = 1;
    uvs[(base + 1) * 2 + 1] = t;
    if (i < segments) indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

/**
 * An empty ribbon whose spine is rewritten every frame from where the bolt has
 * actually been. A wake that is drawn from the flight instead of predicted from it
 * curves when the flight curves, which is the whole reason the bolt reads as an
 * object rather than as an interpolation.
 */
function wakeGeometry(segments) {
  const positions = new Float32Array((segments + 1) * 2 * 3);
  const uvs = new Float32Array((segments + 1) * 2 * 2);
  const indices = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const base = i * 2;
    uvs[base * 2] = 0;
    uvs[base * 2 + 1] = t;
    uvs[(base + 1) * 2] = 1;
    uvs[(base + 1) * 2 + 1] = t;
    if (i < segments) indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

/** Two crossed curtains standing on a cell, for something descending onto it. */
function crossShaftGeometry(radius, height) {
  const merged = new BufferGeometry();
  const a = new PlaneGeometry(radius * 2, height, 1, 16);
  a.translate(0, height / 2, 0);
  const b = a.clone();
  b.rotateY(Math.PI / 2);

  const positions = new Float32Array(
    a.getAttribute('position').array.length + b.getAttribute('position').array.length,
  );
  positions.set(a.getAttribute('position').array, 0);
  positions.set(b.getAttribute('position').array, a.getAttribute('position').array.length);
  const uvs = new Float32Array(a.getAttribute('uv').array.length * 2);
  uvs.set(a.getAttribute('uv').array, 0);
  uvs.set(b.getAttribute('uv').array, a.getAttribute('uv').array.length);
  const offset = a.getAttribute('position').count;
  const indices = [
    ...Array.from(a.getIndex().array),
    ...Array.from(b.getIndex().array).map(value => value + offset),
  ];
  merged.setAttribute('position', new BufferAttribute(positions, 3));
  merged.setAttribute('uv', new BufferAttribute(uvs, 2));
  merged.setIndex(indices);
  a.dispose();
  b.dispose();
  return merged;
}

/** A crescent swept around a cell, for a swing. */
function crescentGeometry(radius, halfAngle, segments = 32, height = 0.42, rake = 0) {
  const positions = new Float32Array((segments + 1) * 2 * 3);
  const uvs = new Float32Array((segments + 1) * 2 * 2);
  const indices = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = -halfAngle + halfAngle * 2 * t;
    /* The blade bellies out through the middle of the swing and closes at the tips. */
    const thickness = 0.11 + 0.22 * Math.sin(t * Math.PI);
    const inner = radius - thickness;
    /*
     * `rake` tilts the plane of the swing: the arc starts high and finishes low, or
     * the reverse. A crescent lying flat is a ring on the floor whichever way it is
     * turned, and a ring is the one thing a swing must not look like.
     */
    const y = height + rake * (t - 0.5);
    const base = i * 2;
    positions[base * 3] = Math.cos(angle) * inner;
    positions[base * 3 + 1] = y;
    positions[base * 3 + 2] = Math.sin(angle) * inner;
    positions[(base + 1) * 3] = Math.cos(angle) * (radius + thickness);
    positions[(base + 1) * 3 + 1] = y;
    positions[(base + 1) * 3 + 2] = Math.sin(angle) * (radius + thickness);
    uvs[base * 2] = t;
    uvs[base * 2 + 1] = 0;
    uvs[(base + 1) * 2] = t;
    uvs[(base + 1) * 2 + 1] = 1;
    if (i < segments) indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

/*
 * Two-sided, for the same reason the grid materials are.
 *
 * The billboard's vertex shader writes screen coordinates directly and flips the
 * vertical axis, which reverses the winding of every quad it draws. A front-facing
 * plane comes out back-facing, and a single-sided material then culls it: the muzzle
 * flare and the bolt's own head were being built, animated and disposed of without
 * ever reaching a pixel.
 */
function billboard(uniforms, fragmentShader) {
  return new Mesh(new PlaneGeometry(1, 1), new ShaderMaterial({
    uniforms,
    vertexShader: BILLBOARD_VERTEX,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(uniforms),
    side: DoubleSide,
    blending: AdditiveBlending,
  }));
}

/** Sparks shed along the arc, budgeted by weight. */
function pathSparks(uniforms, cast, from, to, lift, flightSeconds, spawnHeight = 0.45) {
  const count = Math.round(26 + cast.weight * 34);
  const spark = new Float32Array(count * 4);
  const velocity = new Float32Array(count * 3);
  const positions = new Float32Array(count * 3);
  let seed = (cast.seed || 1) * 9973;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
  for (let i = 0; i < count; i++) {
    spark[i * 4] = random() * 0.94;
    spark[i * 4 + 1] = 0.22 + random() * 0.34;
    spark[i * 4 + 2] = 0.05 + random() * 0.09;
    spark[i * 4 + 3] = random();
    const turn = random() * Math.PI * 2;
    const speed = 0.4 + random() * 1.1;
    velocity[i * 3] = Math.cos(turn) * speed * 0.5;
    velocity[i * 3 + 1] = -0.2 - random() * 0.5;
    velocity[i * 3 + 2] = Math.sin(turn) * speed * 0.5;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('aSpark', new BufferAttribute(spark, 4));
  geometry.setAttribute('aVelocity', new BufferAttribute(velocity, 3));

  const material = new ShaderMaterial({
    uniforms: {
      ...uniforms,
      uFrom: { value: [from.x, spawnHeight, from.z] },
      uTo: { value: [to.x, spawnHeight, to.z] },
      uLift: { value: lift },
      uProgressSeconds: { value: 0 },
      uFlightSeconds: { value: flightSeconds },
    },
    vertexShader: /* glsl */`
      uniform float uProgressSeconds;
      uniform float uFlightSeconds;
      ${PATH_SPARK_VERTEX}
    `,
    fragmentShader: SPARK_FRAGMENT,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(uniforms),
    blending: AdditiveBlending,
  });

  const points = new Points(geometry, material);
  points.frustumCulled = false;
  return points;
}

function seededStream(seed) {
  let state = ((seed || 1) * 6151) % 4294967296;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

/**
 * A ring of motes travelling toward one point, or away from it.
 *
 * `v0 = drag * distance` is not a tuning constant: with linear drag a particle's
 * total travel is exactly v0/k, so aiming it that way lands it on its destination
 * with its speed going to zero there. An inward mote therefore settles into the
 * subject instead of shooting through them, and an outward one runs out of momentum
 * where the effect ends rather than at the edge of the board.
 */
function radialMotes({
  uniforms, cast, at, radius = 2.0, seconds = 1.0, mode = 'in', count = 96,
  rise = 0.5, drag = 2.6, gravity = 0.5, height = 1.0, size = [0.05, 0.11],
  /*
   * How much of the radius the spawn ring is spread over. Wide is a filled disc of
   * motes, which at a distance is scattered confetti however each one moves; narrow
   * is a shell that visibly closes, and closing is the whole statement of an inward
   * gathering.
   */
  band = 0.5,
}) {
  const random = seededStream(cast.seed);
  const inward = mode === 'in';
  const positions = new Float32Array(count * 3);
  const velocity = new Float32Array(count * 3);
  const mote = new Float32Array(count * 3);

  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + random() * 0.6;
    const start = inward ? radius * (1.0 - band + band * random()) : radius * 0.06 * random();
    const travel = inward ? start : radius * (0.45 + 0.65 * random());
    const speed = drag * travel;
    const sign = inward ? -1 : 1;

    positions[i * 3] = at.x + Math.cos(angle) * start;
    positions[i * 3 + 1] = inward ? 0.05 + random() * height : 0.08 + random() * 0.22;
    positions[i * 3 + 2] = at.z + Math.sin(angle) * start;

    velocity[i * 3] = Math.cos(angle) * speed * sign;
    velocity[i * 3 + 1] = rise * (0.25 + random());
    velocity[i * 3 + 2] = Math.sin(angle) * speed * sign;

    mote[i * 3] = random() * seconds * 0.4;
    mote[i * 3 + 1] = seconds * (0.55 + 0.45 * random());
    mote[i * 3 + 2] = size[0] + (size[1] - size[0]) * random();
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('aVelocity', new BufferAttribute(velocity, 3));
  geometry.setAttribute('aMote', new BufferAttribute(mote, 3));

  const points = new Points(geometry, new ShaderMaterial({
    uniforms: {
      ...uniforms,
      uProgressSeconds: { value: 0 },
      uDrag: { value: drag },
      uGravity: { value: gravity },
    },
    vertexShader: RADIAL_VERTEX,
    fragmentShader: MOTE_FRAGMENT,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(uniforms),
    blending: AdditiveBlending,
  }));
  points.frustumCulled = false;
  return points;
}

/** The lit centre a gathering builds, or a displacement re-forms into. */
function coreBillboard(uniforms, at, scale) {
  const mesh = billboard({
    ...uniforms,
    uHead: { value: [at.x, 0.55, at.z] },
    uScale: { value: scale },
    uSprite: { value: null },
    uHasSprite: { value: 0 },
    uSpin: { value: 0 },
  }, HEAD_FRAGMENT);
  mesh.frustumCulled = false;
  return mesh;
}

/**
 * An arrival that is not an impact.
 *
 * Nothing was thrown, so there is no line for debris to leave along and no direction
 * for a cone to open in. What happens instead is radial and slow: light closes on a
 * point and the point comes back lit (`in`), or the ground gives something up and
 * the dust settles outward (`out`).
 */
export function createConverge({
  shared, cast, at, group, mode = 'in', radius = 1.8, durationMs = 760,
}) {
  const uniforms = traceUniforms(shared, cast);
  const inward = mode === 'in';
  const seconds = durationMs / 1000;
  const objects = [];

  const motes = radialMotes({
    uniforms,
    cast,
    at,
    radius,
    seconds,
    mode,
    count: inward ? 88 : 104,
    rise: inward ? 0.5 : 0.85,
    drag: inward ? 2.8 : 3.2,
    gravity: inward ? 0.4 : 2.4,
    height: inward ? 1.1 : 0.4,
  });
  motes.renderOrder = 10;
  group.add(motes);
  objects.push(motes);

  const baseScale = (inward ? 2.2 : 2.6) + cast.weight * 1.1;
  const core = coreBillboard(uniforms, at, baseScale * (inward ? 0.35 : 0.5));
  core.renderOrder = 10;
  group.add(core);
  objects.push(core);

  let elapsed = 0;
  return {
    advance(dt) {
      elapsed += dt;
      const progress = Math.min(1, elapsed / durationMs);
      uniforms.uTime.value += dt / 1000;
      uniforms.uProgress.value = progress;
      motes.material.uniforms.uProgressSeconds.value = elapsed / 1000;
      /* Inward: the centre swells as its light arrives. Outward: it opens and gives
         itself back, which is the same curve read the other way. */
      const eased = progress * progress * (3 - 2 * progress);
      core.material.uniforms.uScale.value = baseScale * (inward ? 0.35 + eased * 0.75 : 0.5 + eased * 1.1);
      const held = inward ? Math.min(1, progress / 0.75) : 1 - eased * 0.35;
      uniforms.uFade.value = Math.max(0, (inward ? held : 1)) * Math.max(0, 1 - Math.max(0, progress - 0.7) / 0.3);
      return elapsed >= durationMs;
    },
    dispose() {
      objects.forEach(object => {
        group.remove(object);
        object.geometry?.dispose();
        object.material?.dispose();
      });
      objects.length = 0;
    },
  };
}

/**
 * One trace, from spawn to arrival and then out.
 *
 * `advance` is driven by the stage's clock rather than by its own timer, so a trace
 * pauses when the tab does and cannot outlive the effect it belongs to. After the
 * arrival the trace is RELEASED rather than destroyed: the air a shot went through
 * is still bent for a tenth of a second after the shot has landed, and cutting the
 * beam at the exact frame of impact is what made the old one look switched off.
 */
export class Trace {
  constructor({ shared, cast, from, to, group }) {
    this.cast = cast;
    this.group = group;
    this.elapsed = 0;
    this.afterglow = 0;
    this.released = false;
    this.duration = Math.max(1, cast.traceMs);
    /* A gathering is still closing when the effect lands; cutting it on the frame of
       arrival throws away the half of the motion that reads as absorption. */
    this.afterglowMs = cast.trace === 'beam' ? 220 : (cast.trace === 'gather' ? 460 : 140);
    this.objects = [];
    this.from = from;
    this.to = to;
    this.uniforms = traceUniforms(shared, cast);

    const kind = cast.trace;
    if (kind === 'none') return;

    const facing = { x: to.x - from.x, z: to.z - from.z };
    const length = Math.hypot(facing.x, facing.z) || 1;
    this.axis = { x: facing.x / length, z: facing.z / length };

    if (kind === 'beam') {
      /* Metal reads as a travelling slug; everything else as light written out. */
      const tracer = normalizeViolationType(cast.violationType) === 'Physical' ? 1 : 0;
      const mouth = (tracer ? 0.10 : 0.14) + cast.weight * 0.09;
      Object.assign(this.uniforms, {
        uFrom: { value: [from.x, BEAM_HEIGHT, from.z] },
        uTo: { value: [to.x, BEAM_HEIGHT, to.z] },
        uStartHalf: { value: mouth },
        uEndHalf: { value: tracer ? mouth * 0.75 : 0.04 },
        uTracer: { value: tracer },
      });
      const mesh = new Mesh(
        beamStrip(),
        new ShaderMaterial({
          uniforms: this.uniforms,
          vertexShader: BEAM_VERTEX,
          fragmentShader: BEAM_FRAGMENT,
          transparent: true,
          depthWrite: false,
          ...projectionOptions(this.uniforms),
          side: DoubleSide,
          blending: AdditiveBlending,
        }),
      );
      mesh.renderOrder = 7;
      mesh.frustumCulled = false;
      this.add(mesh);
      this.addMuzzle();
    } else if (kind === 'orb') {
      this.lift = 0.55 + cast.weight * 0.7;
      this.wakeSegments = 26;
      this.history = [];
      const wake = new Mesh(
        wakeGeometry(this.wakeSegments),
        gridMaterial(this.uniforms, WAKE_FRAGMENT),
      );
      wake.renderOrder = 7;
      wake.frustumCulled = false;
      this.wake = wake;
      this.add(wake);

      this.sparks = pathSparks(this.uniforms, cast, from, to, this.lift, this.duration / 1000);
      this.sparks.renderOrder = 8;
      this.add(this.sparks);

      const head = billboard({
        ...this.uniforms,
        uHead: { value: [from.x, 0.45, from.z] },
        uScale: { value: 0.5 + cast.weight * 0.55 },
        uSprite: { value: spriteTexture(cast.sprite) },
        uHasSprite: { value: cast.sprite ? 1 : 0 },
        uSpin: { value: 0 },
      }, HEAD_FRAGMENT);
      head.renderOrder = 9;
      head.frustumCulled = false;
      this.head = head;
      this.add(head);
      this.addMuzzle(0.55);
    } else if (kind === 'charge') {
      const mesh = new Mesh(
        taperedRibbon(from, to, 0.1 + cast.weight * 0.08, 0.3 + cast.weight * 0.22, 30, 0.06, 0.2),
        gridMaterial(this.uniforms, CHARGE_FRAGMENT),
      );
      mesh.renderOrder = 7;
      mesh.frustumCulled = false;
      this.add(mesh);

      /* Grit torn off the floor along the run, thrown low and left behind. */
      this.sparks = pathSparks(this.uniforms, cast, from, to, 0.1, this.duration / 1000, 0.12);
      this.sparks.renderOrder = 8;
      this.add(this.sparks);
      this.addMuzzle(0.7);
    } else if (kind === 'gather') {
      /* The subject is the destination, so everything is built around `to`. */
      this.motes = radialMotes({
        uniforms: this.uniforms,
        cast,
        at: to,
        /*
         * Sized to the effect, not fixed. A gathering whose ring stands two and a
         * half cells out from a single-cell heal reads as unrelated specks in the
         * dark — the motes have to visibly belong to the figure they are closing on.
         */
        radius: 1.25 + Math.max(0, (cast.size || 1) - 1) * 0.75 + cast.weight * 0.6,
        seconds: this.duration / 1000,
        mode: 'in',
        count: 170,
        rise: 0.55,
        drag: 2.4,
        gravity: 0.55,
        height: 0.85,
        band: 0.22,
        /*
         * Bigger than debris. A gathering has no beam, no arc and no shockfront —
         * the motes ARE the effect, and at the size a spark is thrown at they came
         * out as a couple of pixels of dust and the whole action read as nothing
         * happening.
         */
        size: [0.055, 0.115],
      });
      this.motes.renderOrder = 8;
      this.add(this.motes);

      this.coreBase = 0.7 + cast.weight * 0.8;
      this.core = coreBillboard(this.uniforms, to, this.coreBase * 0.25);
      this.core.renderOrder = 9;
      this.add(this.core);
    } else if (kind === 'collapse') {
      const mesh = new Mesh(
        crossShaftGeometry(0.46 + cast.weight * 0.2, 2.0),
        gridMaterial(this.uniforms, COLLAPSE_FRAGMENT),
      );
      mesh.position.set(from.x, 0, from.z);
      mesh.renderOrder = 7;
      mesh.frustumCulled = false;
      this.add(mesh);

      /* Pulled off the floor around the feet, not thrown: the subject is going. */
      this.motes = radialMotes({
        uniforms: this.uniforms,
        cast,
        at: from,
        radius: 1.3,
        seconds: this.duration / 1000,
        mode: 'in',
        count: 90,
        rise: 1.1,
        drag: 3.4,
        gravity: 0.15,
        height: 0.9,
        size: [0.055, 0.12],
      });
      this.motes.renderOrder = 8;
      this.add(this.motes);
    } else if (kind === 'rift') {
      const mesh = new Mesh(
        crossShaftGeometry(0.5 + cast.weight * 0.3, 2.5),
        gridMaterial(this.uniforms, RIFT_FRAGMENT),
      );
      mesh.position.set(to.x, 0, to.z);
      mesh.renderOrder = 7;
      mesh.frustumCulled = false;
      this.add(mesh);

      this.motes = radialMotes({
        uniforms: this.uniforms,
        cast,
        at: to,
        radius: 1.25,
        seconds: this.duration / 1000,
        mode: 'out',
        count: 84,
        rise: 0.7,
        drag: 3.0,
        gravity: 1.8,
        height: 0.3,
      });
      this.motes.renderOrder = 8;
      this.add(this.motes);
    } else if (kind === 'descend') {
      const mesh = new Mesh(
        crossShaftGeometry(0.5 + cast.weight * 0.35, 3.4),
        gridMaterial(this.uniforms, SHAFT_FRAGMENT),
      );
      mesh.position.set(to.x, 0, to.z);
      mesh.renderOrder = 7;
      mesh.frustumCulled = false;
      this.add(mesh);
    }
  }

  addMuzzle(scale = 1) {
    if (!this.cast.casterKick) return;
    const mesh = billboard({
      ...this.uniforms,
      uHead: { value: [this.from.x, BEAM_HEIGHT, this.from.z] },
      uScale: { value: (1.1 + this.cast.weight * 0.7) * scale },
      uAxis: { value: [this.axis.x, -this.axis.z * 0.55] },
      /* Its own clock: sharing uProgress meant the flare was driving the beam's
         write-out and the beam was driving the flare's decay. */
      uMuzzle: { value: 0 },
    }, MUZZLE_FRAGMENT);
    mesh.renderOrder = 9;
    mesh.frustumCulled = false;
    this.muzzle = mesh;
    this.muzzleElapsed = 0;
    this.add(mesh);
  }

  add(object) {
    this.objects.push(object);
    this.group.add(object);
  }

  /** Arrival happened; play out what the air is still doing. */
  release() {
    if (this.released) return;
    this.released = true;
    this.afterglow = 0;
    if (this.head) this.head.visible = false;
  }

  advance(dt) {
    this.uniforms.uTime.value += dt / 1000;
    if (this.released) {
      this.afterglow += dt;
      const fade = Math.max(0, 1 - this.afterglow / this.afterglowMs);
      this.uniforms.uFade.value = fade * fade;
      if (this.muzzle) this.stepMuzzle(dt);
      /* Motes are still in flight when the arrival happens — a rift's dust does not
         stop in the air because the thing it let through has landed. */
      if (this.motes) {
        this.motes.material.uniforms.uProgressSeconds.value = (this.elapsed + this.afterglow) / 1000;
      }
      return this.afterglow >= this.afterglowMs;
    }

    this.elapsed += dt;
    const progress = Math.min(1, this.elapsed / this.duration);
    this.uniforms.uProgress.value = progress;
    if (this.muzzle) this.stepMuzzle(dt);
    /* Sparks run on their own seconds clock, and not every trace that sheds them
       carries a head — the charge is a front with nothing riding it. */
    if (this.sparks) this.sparks.material.uniforms.uProgressSeconds.value = this.elapsed / 1000;
    if (this.motes) this.motes.material.uniforms.uProgressSeconds.value = this.elapsed / 1000;
    if (this.core) {
      /* The gathered centre is built by what arrives, so it swells with the flight
         rather than being switched on. */
      const eased = progress * progress * (3 - 2 * progress);
      this.core.material.uniforms.uScale.value = this.coreBase * (0.25 + eased * 0.9);
    }

    if (this.head) {
      const eased = progress * progress * (3 - 2 * progress);
      const x = this.from.x + (this.to.x - this.from.x) * eased;
      const z = this.from.z + (this.to.z - this.from.z) * eased;
      const y = 0.45 + Math.sin(eased * Math.PI) * this.lift;
      this.head.material.uniforms.uHead.value = [x, y, z];
      this.head.material.uniforms.uSpin.value = this.elapsed * 0.005;
      this.trackWake(x, y, z);
    }

    return progress >= 1;
  }

  stepMuzzle(dt) {
    this.muzzleElapsed += dt;
    const life = Math.min(1, this.muzzleElapsed / 150);
    this.muzzle.material.uniforms.uMuzzle.value = life;
    this.muzzle.visible = life < 1;
  }

  /**
   * The wake, rewritten from where the bolt has been.
   *
   * The ribbon is widened per sample rather than uniformly, so the trail is fat
   * behind the head and pinches out at the tail like something dissipating.
   */
  trackWake(x, y, z) {
    this.history.unshift({ x, y, z });
    if (this.history.length > this.wakeSegments + 1) this.history.length = this.wakeSegments + 1;
    const attribute = this.wake.geometry.getAttribute('position');
    const half = 0.11 + this.cast.weight * 0.09;
    for (let i = 0; i <= this.wakeSegments; i++) {
      const point = this.history[Math.min(i, this.history.length - 1)];
      const ahead = this.history[Math.max(0, Math.min(i - 1, this.history.length - 1))];
      let nx = -(point.z - ahead.z);
      let nz = point.x - ahead.x;
      const length = Math.hypot(nx, nz);
      if (length < 1e-4) {
        nx = -this.axis.z;
        nz = this.axis.x;
      } else {
        nx /= length;
        nz /= length;
      }
      const width = half * (1 - i / (this.wakeSegments + 1));
      const base = i * 2;
      attribute.setXYZ(base, point.x - nx * width, point.y, point.z - nz * width);
      attribute.setXYZ(base + 1, point.x + nx * width, point.y, point.z + nz * width);
    }
    attribute.needsUpdate = true;
  }

  dispose() {
    this.objects.forEach(object => {
      this.group.remove(object);
      object.geometry?.dispose();
      object.material?.dispose();
    });
    this.objects = [];
  }
}

/**
 * The swing itself, played at the target once the charge has landed.
 *
 * Two things make it read as a blow rather than as a decal appearing on a cell.
 *
 * The arc's CENTRE sits back toward the attacker, not on the target: a crescent
 * centred on the victim is a ring around them, and its belly — the part the eye
 * reads as the blade — never touches them. Pulled back by most of its own radius the
 * belly lands squarely on the body and the arms wrap past both flanks, which is a
 * blade going through someone.
 *
 * And it is RAKED, not flat, so the swing has a high side and a low side.
 *
 * Physical blows swing lower and flatter than blades do — a boot and a fist arrive
 * at hip height on a straight line, an edge comes down across the body — so the
 * violation the game already resolved sets the plane rather than a per-skill table.
 *
 * Which way up, and exactly how high, come off a hash of the skill's own name. Every
 * unarmed blow in the book is grade-9 Physical damage over one cell, so violation and
 * weight give the renderer nothing to tell "Right Hook" from "Front Kick" with — and
 * eight identical swings in a school is the complaint that a spray of the same decal
 * on every skill earned. Hashing the name rather than the cast seed is what makes it
 * an IDENTITY: a hook comes down the same way every time it is thrown, instead of
 * flickering between two arcs on consecutive casts.
 */
function nameSwing(name) {
  let hash = 0;
  const text = String(name || '');
  for (let i = 0; i < text.length; i++) hash = (Math.imul(hash, 31) + text.charCodeAt(i)) | 0;
  const unit = (hash >>> 0) / 4294967296;
  return { flip: (hash & 1) ? 1 : -1, unit };
}

export function createSlash({ shared, cast, at, facing, group }) {
  const uniforms = traceUniforms(shared, cast);
  const radius = 0.7 + cast.weight * 0.45;
  const bodyBlow = cast.violationType === 'Physical' || cast.violationType === 'None';
  const { flip, unit } = nameSwing(cast.name);
  /*
   * A hundred and twenty degrees, not the full half-circle the first version swept.
   * Past about that the tips point back BEHIND the centre of the arc, so the front
   * spends most of its travel out on open ground either side of the target and only
   * crosses the body in passing — which is what made the swing read as something
   * flying past rather than something landing.
   */
  const mesh = new Mesh(
    crescentGeometry(
      radius,
      Math.PI * (0.31 + unit * 0.07),
      36,
      (bodyBlow ? 0.22 : 0.48) + unit * 0.16,
      (bodyBlow ? 0.13 + unit * 0.09 : 0.26 + unit * 0.14) * flip,
    ),
    new ShaderMaterial({
      uniforms,
      vertexShader: GRID_VERTEX,
      fragmentShader: SLASH_FRAGMENT,
      transparent: true,
      depthWrite: false,
      ...projectionOptions(uniforms),
      side: DoubleSide,
      blending: AdditiveBlending,
    }),
  );
  const back = radius * 0.95;
  mesh.position.set(at.x - Math.cos(facing) * back, 0, at.z - Math.sin(facing) * back);
  /* Local +x is the belly of the crescent; turn it to point down the attack line. */
  mesh.rotation.y = -facing;
  mesh.renderOrder = 9;
  mesh.frustumCulled = false;
  mesh.userData.uniforms = uniforms;
  group.add(mesh);
  return mesh;
}

/** Drive one swing's front around its arc, and fade it out behind. */
export function advanceSlash(mesh, elapsed, duration) {
  const uniforms = mesh.userData.uniforms;
  if (!uniforms) return;
  const progress = Math.min(1.35, elapsed / duration);
  uniforms.uProgress.value = progress;
  uniforms.uTime.value = elapsed / 1000;
  uniforms.uFade.value = Math.max(0, 1 - Math.max(0, progress - 0.75) / 0.6);
}

/**
 * The moment of arrival, in the air.
 *
 * A single-cell hit gets no lit body on the board — there is no shape one cell is —
 * so everything it has to say happens here, and if this is missing the whole action
 * resolves into a smudge on the floor under a figure standing above it. What sells
 * it is a hot bloom at the height the shot was travelling at, over in a fifth of a
 * second, and debris leaving that point along the line the shot came in on.
 */
const BLOOM_FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUvv;
  ${TRACE_HEADER}
  ${LIFECYCLE_CHUNK}
  ${EMISSION_CHUNK}

  void main(){
    vec2 q = (vUvv - 0.5) * 2.0;
    float life = clamp(uProgress, 0.0, 1.0);

    /* Opens hard, then gives it all back: an impact is an event, not a light. */
    float grow = 0.30 + life * 0.85;
    float fall = pow(1.0 - life, 2.3);
    float r = length(q) / grow;

    float core = exp(-r * r * 24.0);
    float bloom = exp(-r * r * 3.2);
    /*
     * Blown light, not a ring. A ring is the shape an impact never makes, and the
     * lobes are wide and soft enough that no individual spike can be counted.
     */
    float lobes = exp(-r * r * 1.5) * pow(abs(cos(atan(q.y, q.x) * 2.0 + uSeed)), 4.0);

    float heat = 0.2 + core * 1.3 + bloom * 0.4 + lobes * 0.3;
    vec3 color = emberRamp(heat);
    float alpha = (core * 1.05 + bloom * 0.36 + lobes * 0.3) * fall;
    alpha *= uActivation * (0.55 + 0.6 * uIntensity);
    if (alpha < 0.005) discard;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

/**
 * Debris thrown by the arrival, each piece on its own decelerating arc.
 *
 * Same closed-form integration as the flight sparks — one clock, per-particle
 * constants, nothing uploaded per frame — so what the eye follows is a real
 * ballistic path with a decelerating head rather than a scatter of dots fading in
 * place. Size falls over the life and the colour cools with it, which is what makes
 * a spark read as something that was thrown rather than something that was drawn.
 */
const SPRAY_VERTEX = /* glsl */`
  ${PROJECTION_CHUNK}
  ${LIFECYCLE_CHUNK}
  ${PARTICLE_PATH_CHUNK}
  uniform float uSeconds;
  uniform vec3 uAt;
  uniform float uDrag;
  uniform float uGravity;
  attribute vec3 aVelocity;
  attribute vec3 aSpark;
  varying float vLife;

  void main(){
    float age = uSeconds - aSpark.x;
    if (age < 0.0 || age > aSpark.y){
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vLife = 1.0;
      return;
    }
    vec3 p = dragPath(uAt, aVelocity, uDrag, uGravity, age);
    vLife = clamp(age / aSpark.y, 0.0, 1.0);
    gl_Position = gridToClip(p);
    gl_PointSize = clamp(aSpark.z * (1.0 - vLife * 0.62) * uCellPx, 1.0, 26.0);
  }
`;

/** How the debris of each material behaves once it is off the ground. */
const SPRAY_STYLES = {
  Physical: { speed: 3.4, spread: 1.5, rise: 2.2, drag: 2.4, gravity: 7.0, life: [0.26, 0.7], size: [0.035, 0.085] },
  Energy: { speed: 4.2, spread: 2.0, rise: 1.9, drag: 4.4, gravity: 3.0, life: [0.2, 0.5], size: [0.04, 0.09] },
  Light: { speed: 2.0, spread: 1.3, rise: 2.4, drag: 3.0, gravity: -0.6, life: [0.35, 0.85], size: [0.05, 0.11] },
  Darkness: { speed: 2.4, spread: 1.7, rise: 1.4, drag: 3.2, gravity: 2.0, life: [0.35, 0.85], size: [0.06, 0.13] },
  Heat: { speed: 2.4, spread: 1.6, rise: 2.8, drag: 3.0, gravity: -1.2, life: [0.35, 0.9], size: [0.05, 0.12] },
  Cold: { speed: 1.6, spread: 1.3, rise: 1.2, drag: 3.6, gravity: 1.8, life: [0.4, 0.9], size: [0.045, 0.1] },
  Mental: { speed: 1.4, spread: 1.6, rise: 1.1, drag: 2.8, gravity: -0.4, life: [0.45, 1.0], size: [0.05, 0.12] },
  None: { speed: 1.8, spread: 1.3, rise: 1.4, drag: 3.2, gravity: 1.4, life: [0.35, 0.8], size: [0.05, 0.1] },
};

export function sprayStyle(violation) {
  return SPRAY_STYLES[normalizeViolationType(violation)] || SPRAY_STYLES.None;
}

/**
 * One arrival: the bloom and the debris, on one clock, released together.
 *
 * Built here rather than in the ground effect because it is not ground — it belongs
 * to the same journey as the trace, ends where the trace ends, and must not claim a
 * cell the server never resolved.
 */
export function createArrival({ shared, cast, at, axis, group }) {
  const uniforms = traceUniforms(shared, cast);
  const style = sprayStyle(cast.violationType);
  const objects = [];
  const bloomMs = 210 + cast.weight * 90;

  const bloom = billboard({
    ...uniforms,
    uHead: { value: [at.x, BEAM_HEIGHT, at.z] },
    uScale: { value: 1.8 + cast.weight * 1.5 },
  }, BLOOM_FRAGMENT);
  bloom.renderOrder = 10;
  bloom.frustumCulled = false;
  group.add(bloom);
  objects.push(bloom);

  const count = Math.round(44 + cast.weight * 46);
  const spark = new Float32Array(count * 3);
  const velocity = new Float32Array(count * 3);
  const positions = new Float32Array(count * 3);
  let seed = ((cast.seed || 1) * 7919) % 4294967296;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
  const dirX = axis?.x ?? 1;
  const dirZ = axis?.z ?? 0;
  let longest = 0;
  for (let i = 0; i < count; i++) {
    /* A fifth splash back down the line: a pure forward cone reads as a sprayer. */
    const back = random() < 0.22 ? -0.5 : 1;
    const forward = style.speed * (0.3 + 0.7 * random()) * back;
    const turn = random() * Math.PI * 2;
    const scatter = style.spread * random() * random();
    velocity[i * 3] = dirX * forward + Math.cos(turn) * scatter;
    velocity[i * 3 + 1] = style.rise * (0.2 + 0.9 * random());
    velocity[i * 3 + 2] = dirZ * forward + Math.sin(turn) * scatter;

    const delay = random() < 0.85 ? random() * 0.04 : 0.04 + random() * 0.12;
    const life = style.life[0] + (style.life[1] - style.life[0]) * random();
    spark[i * 3] = delay;
    spark[i * 3 + 1] = life;
    spark[i * 3 + 2] = style.size[0] + (style.size[1] - style.size[0]) * random();
    longest = Math.max(longest, delay + life);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('aVelocity', new BufferAttribute(velocity, 3));
  geometry.setAttribute('aSpark', new BufferAttribute(spark, 3));

  const sprayUniforms = {
    ...uniforms,
    uAt: { value: [at.x, BEAM_HEIGHT * 0.75, at.z] },
    uSeconds: { value: 0 },
    uDrag: { value: style.drag },
    uGravity: { value: style.gravity },
  };
  const spray = new Points(geometry, new ShaderMaterial({
    uniforms: sprayUniforms,
    vertexShader: SPRAY_VERTEX,
    fragmentShader: SPARK_FRAGMENT,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(sprayUniforms),
    blending: AdditiveBlending,
  }));
  spray.renderOrder = 10;
  spray.frustumCulled = false;
  group.add(spray);
  objects.push(spray);

  const totalMs = Math.max(bloomMs, longest * 1000 + 40);
  let elapsed = 0;

  return {
    advance(dt) {
      elapsed += dt;
      uniforms.uTime.value += dt / 1000;
      uniforms.uProgress.value = Math.min(1, elapsed / bloomMs);
      sprayUniforms.uSeconds.value = elapsed / 1000;
      /* The debris outlives the bloom, so the two fades are separate clocks. */
      uniforms.uFade.value = Math.max(0, 1 - Math.max(0, elapsed - totalMs * 0.6) / (totalMs * 0.4));
      return elapsed >= totalMs;
    },
    dispose() {
      objects.forEach(object => {
        group.remove(object);
        object.geometry?.dispose();
        object.material?.dispose();
      });
      objects.length = 0;
    },
  };
}

export const TRACE_KINDS = { beam: 0, orb: 1, descend: 2, slash: 3 };
